import {
  GridDataStateChangeEvent,
  GridHeaderSelectionChangeEvent,
  GridItemChangeEvent,
  GridPagerSettings,
  GridRowProps,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid';
import { GridSelectableSettings } from '@progress/kendo-react-grid/dist/npm/interfaces/GridSelectableSettings';
import { cloneElement, useCallback, useState } from 'react';
import ocrScanManagerColumns from 'src/assets/static/invoice_scan_manager/ocrScanManagerColumns';
import GridWrapper from 'src/components/GridWrapper';
import useCompany from 'src/hooks/useCompany';
import useInvoiceScanManager from 'src/hooks/useInvoiceScanManager';
import useWindowSize from 'src/hooks/useWindowSize';
import OcrQueueTableToolBar from './OcrQueueTableToolBar';

const selectable: GridSelectableSettings = {
  enabled: true,
  drag: false,
  cell: false,
  mode: 'multiple',
};

const gridPagerSettings: GridPagerSettings = {
  buttonCount: 5,
  pageSizes: [10, 20, 50, 100],
};

const rowRender = (trElement: React.ReactElement<HTMLTableRowElement>, props: GridRowProps) => {
  let trProps: any = {};
  trProps = {
    ...trElement.props,
    className: `${trElement.props.className} ${props.className} ${
      props.dataItem.preview ? 'border-preview-box' : ''
    }`,
  };
  return cloneElement(trElement, { ...trProps });
};

const defaultProps = {
  tableTextId: 'Records',
  DataItemKey: 'rowId',
  sortable: true,
  resizable: true,
  showColumnMenu: true,
  selectable: selectable,
  GridPagerSettings: gridPagerSettings,
  rowRender,
} as const;

const supportEmail = 'iBuyEfficientSupport@avendra.com';
const mailSubject = 'Enrollment in OCR Service for Auto-Generated Invoices';

const OcrQueueTable = () => {
  const {
    ocrCompanies,
    isOcrCompany,
    ocrAttachmentList,
    setOcrAttachmentList,
    ocrDataState,
    setOcrDataState,
    count,
    isAttachDocumentLoading,
    isOcrQueueAutoReloading,
    refreshPage,
    showPrev,
  } = useInvoiceScanManager();
  const { height } = useWindowSize();
  const { company } = useCompany();
  const [latestClickedId, setLatestClickedId] = useState(0);

  const mailBody = [
    'Dear iBuyEfficient Support,',
    '',
    `Our company, ${
      company.companyName.split('(')[0]
    } is not enrolled in the OCR service for scanning files and auto-generating invoices. Could you provide information on this feature including information on how to enroll?`,
    '',
    'Thank you.',
  ].join('\n');

  const onHeaderSelectionChange = useCallback(
    (event: GridHeaderSelectionChangeEvent) => {
      const checkboxElement: any = event.syntheticEvent.target;
      const { checked } = checkboxElement;
      setOcrAttachmentList((prev) => prev.map((element) => ({ ...element, selected: checked })));
    },
    [setOcrAttachmentList]
  );

  const onRowSelectionChange = useCallback(
    (event: GridSelectionChangeEvent) => {
      setOcrAttachmentList((prev) => {
        const prevItems = [...prev];
        const index = event?.startRowIndex;
        prevItems[index].selected = !prevItems[index].selected;
        return prevItems;
      });
    },
    [setOcrAttachmentList]
  );

  const onScanManagerDataStateChange = useCallback(
    (e: GridDataStateChangeEvent) => {
      setOcrDataState(e.dataState);
    },
    [setOcrDataState]
  );

  const cellClickFns = useCallback(
    () => ({ latestClickedId, setLatestClickedId, refreshList: refreshPage }),
    [latestClickedId, refreshPage]
  );

  const itemChange = useCallback(
    (event: GridItemChangeEvent) => {
      if (event.field === 'originalFile') {
        let { field } = event;
        event.dataItem[field] = event.value;
        setOcrAttachmentList((prev) =>
          [...prev].map((row: any) => {
            if (row.attachId === event.dataItem.attachId) {
              row[field] = event.value;
            }
            return row;
          })
        );
      }
    },
    [setOcrAttachmentList]
  );

  const handleSelect = () => {
    const encodedSubject = encodeURIComponent(mailSubject);
    const encodedBody = encodeURIComponent(mailBody);
    const mailURL = `mailto:${supportEmail}?subject=${encodedSubject}&body=${encodedBody}`;
    window.location.href = mailURL;
  };

  return (
    <>
      {ocrCompanies.length > 0 && (
        <>
          {isOcrCompany ? (
            <GridWrapper
              columns={ocrScanManagerColumns}
              total={count}
              className="min-h-[300px]"
              dataSource={ocrAttachmentList}
              dataState={ocrDataState}
              onHeaderSelectionChange={onHeaderSelectionChange}
              onItemChange={itemChange}
              onSelectionChange={onRowSelectionChange}
              onDataStateChange={onScanManagerDataStateChange}
              pageable={gridPagerSettings}
              height={`${height - 160}px`}
              gridToolbarChildren={<OcrQueueTableToolBar />}
              fieldModifiable
              cellClick={cellClickFns}
              key={`${showPrev}`}
              {...defaultProps}
              loading={!isOcrQueueAutoReloading ? isAttachDocumentLoading : false}
            />
          ) : (
            <div className="flex flex-row mt-[50px] mx-1">
              <div className="flex flex-col [&>span]:font-medium">
                <span>
                  Your company is not currently enrolled in the OCR service to scan files and
                  auto-generate invoices.
                </span>
                <span>Please contact iBuyEfficient Support for more information.</span>
                <span
                  onClick={handleSelect}
                  className="mt-4 text-info underline cursor-pointer w-0"
                >
                  iBuyEfficientSupport@avendra.com
                </span>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default OcrQueueTable;
